import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import { IAgreement } from "../../../shared/services/api/sinister/SinisterAgreementService";
import { globalMaskDocument } from "../../../shared/utils";
import { formatScaleValueBR } from "../../../shared/utils/formatScaleValue";
import { HtmlTooltip } from "../../../infra/components/tooltipsCustom/tooltipsCuston";
import { getPaymentMethodLabel } from "../../creditRecovery/constants/paymentMethodOptions";
import { getAgreementStatusChipColors, getAgreementStatusLabel } from "../constants/agreementStatusOptions";
import { AgreementRowMenu } from "./AgreementRowMenu";

interface Props {
	rows: IAgreement[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onAgreementChanged: () => void;
}

const TABLE_COLUMN_COUNT = 8;

const getLastInstallmentDueDate = (agreement: IAgreement): string => {
	const last = agreement.installments.reduce(
		(acc, installment) => (installment.number > acc.number ? installment : acc),
		agreement.installments[0]
	);
	return last?.dueDate ?? agreement.dueDate;
};

export const TableAgreements = ({
	rows,
	totalCount,
	page,
	rowsPerPage,
	onPageChange,
	onRowsPerPageChange,
	onAgreementChanged,
}: Props) => {
	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
		>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{["Locatário", "Imobiliária", "Valor do Acordo", "Forma de Pagamento", "Vencimento", "Status", "Cadastro", "Ações"].map(
							(header, i) => (
								<TableCell key={i} align={i <= 1 ? "left" : "center"}>
									<Typography variant="button">{header}</Typography>
								</TableCell>
							)
						)}
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.length === 0 ? (
						<TableRow>
							<TableCell colSpan={TABLE_COLUMN_COUNT} align="center" sx={{ py: 6 }}>
								<Typography variant="body2" color="text.secondary">
									Nenhum acordo encontrado para os filtros aplicados.
								</Typography>
							</TableCell>
						</TableRow>
					) : (
						rows.map((row) => {
						const createdAtDate = parseISO(row.createdAt);
						const firstDueDate = parseISO(row.dueDate);
						const lastDueDate = parseISO(getLastInstallmentDueDate(row));
						const statusColors = getAgreementStatusChipColors(row.status);

						return (
							<TableRow key={row.id} hover>
								<TableCell>
									<HtmlTooltip
										title={
											<Box>
												<Typography variant="subtitle2" fontWeight="bold">
													{row.proposal.name}
												</Typography>
											</Box>
										}
										arrow
										placement="top"
									>
										<Box>
											<Typography variant="body2" sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
												{row.proposal.name}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{globalMaskDocument(row.proposal.document)}
											</Typography>
										</Box>
									</HtmlTooltip>
								</TableCell>

								<TableCell>
									<Typography variant="body2" sx={{ maxWidth: "26ch", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
										{row.client.name}
									</Typography>
								</TableCell>

								<TableCell align="center">
									<Stack spacing={0.2} alignItems="center">
										<Typography variant="body2" fontWeight={600}>
											{formatScaleValueBR(String(row.totalValue))}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{row.installmentsCount === 1 ? "À Vista" : `${row.installmentsCount}x parcelas`}
										</Typography>
									</Stack>
								</TableCell>

								<TableCell align="center">
									<Typography variant="body2">{getPaymentMethodLabel(row.paymentMethod)}</Typography>
								</TableCell>

								<TableCell align="center">
									<Stack spacing={0.2} alignItems="center">
										<Typography variant="body2" color="textSecondary">
											{format(firstDueDate, "dd/MM/yyyy")}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											até {format(lastDueDate, "dd/MM/yyyy")}
										</Typography>
									</Stack>
								</TableCell>

								<TableCell align="center">
									<Chip
										size="medium"
										label={getAgreementStatusLabel(row.status)}
										sx={{ fontWeight: 500, bgcolor: statusColors.bg, color: statusColors.color, border: "0.5px solid #f1f1f1" }}
									/>
								</TableCell>

								<TableCell align="center">
									<Stack spacing={0.2}>
										<Typography variant="body2" color="textSecondary">
											{format(createdAtDate, "dd/MM/yyyy")}
										</Typography>
										<Typography variant="body2" color="textSecondary">
											{format(createdAtDate, "HH:mm:ss")}
										</Typography>
									</Stack>
								</TableCell>

								<TableCell align="center">
									<AgreementRowMenu agreement={row} onChanged={onAgreementChanged} />
								</TableCell>
							</TableRow>
						);
						})
					)}
				</TableBody>
			</Table>

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>
		</TableContainer>
	);
};
